import React, { useEffect, useMemo, useState } from 'react';
import { CHURCH_EVENTS } from '../../data/churchEvents';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import AddCalendarEventModal from './AddCalendarEventModal';
import './ChurchMonthCalendar.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_MOBILE = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MOBILE_BREAKPOINT = 600;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const FASTING_ANCHOR = '2026-05-25';
const FASTING_WEEK_DAYS = 7;

const parseIsoDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const getQuarterlyFastingEvents = (viewYear) => {
  const anchor = parseIsoDate(FASTING_ANCHOR);
  const yearStart = new Date(viewYear, 0, 1);
  const yearEnd = new Date(viewYear, 11, 31);
  const occurrences = [];

  let cursor = new Date(anchor);
  while (cursor > yearEnd) {
    cursor.setMonth(cursor.getMonth() - 3);
  }
  while (cursor > yearStart) {
    cursor.setMonth(cursor.getMonth() - 3);
  }

  let safety = 0;
  while (cursor <= yearEnd && safety < 24) {
    const start = new Date(cursor);
    const end = new Date(cursor);
    end.setDate(end.getDate() + FASTING_WEEK_DAYS - 1);

    if (end >= yearStart && start <= yearEnd) {
      occurrences.push({
        id: `fasting-${toIsoDate(start)}`,
        title: 'Fasting Prayer Week',
        start: toIsoDate(start),
        end: toIsoDate(end),
        color: '#9f1a26',
        highlight: true,
        detailLines: [
          'Every 3 months',
          `${formatDateShort(start)} – ${formatDateShort(end)}`,
          'Prayer and fasting as a church family.',
        ],
      });
    }

    cursor.setMonth(cursor.getMonth() + 3);
    safety += 1;
  }

  return occurrences;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDateShort = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getEventKey = (event, date) => `${event.id}-${date ? date.toISOString() : 'range'}`;

const getEventDetailLines = (event) => {
  const lines = [];

  if (event.detailLines) {
    lines.push(...event.detailLines);
    return lines;
  }

  if (event.start && event.end) {
    const start = parseIsoDate(event.start);
    const end = parseIsoDate(event.end);
    if (start.getTime() !== end.getTime()) {
      lines.push(`${formatDateShort(start)} – ${formatDateShort(end)}`);
    }
  }

  return lines;
};

const getLastSundayOfMonth = (year, month) => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let day = lastDay; day >= 1; day -= 1) {
    if (new Date(year, month, day).getDay() === 0) return day;
  }
  return lastDay;
};

const isSecondWeekdayOfMonth = (year, month, day, weekday) => {
  if (new Date(year, month, day).getDay() !== weekday) return false;
  let count = 0;
  for (let d = 1; d <= day; d += 1) {
    if (new Date(year, month, d).getDay() === weekday) count += 1;
  }
  return count === 2;
};

const getCellEventsForDate = (date, events) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const weekday = date.getDay();
  const matched = [];

  events.forEach((event) => {
    if (event.start && event.end) {
      const start = parseIsoDate(event.start);
      const end = parseIsoDate(event.end);
      if (start.getTime() !== end.getTime()) return;
      if (date >= start && date <= end) matched.push(event);
      return;
    }

    if (event.recurrence === 'weekly' && event.weekday === weekday) {
      matched.push(event);
      return;
    }

    if (event.recurrence === 'lastSunday' && weekday === 0 && day === getLastSundayOfMonth(year, month)) {
      matched.push(event);
      return;
    }

    if (event.recurrence === 'secondWeekday' && isSecondWeekdayOfMonth(year, month, day, event.weekday)) {
      matched.push(event);
    }
  });

  return matched;
};

/** Multi-day events are drawn as a bar spanning the week instead of inside a single cell. */
const getRangeEvents = (events) =>
  events.filter((event) => event.start && event.end && event.start !== event.end);

const buildMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const cells = [];

  for (let i = firstDay - 1; i >= 0; i -= 1) {
    const day = daysInPrevMonth - i;
    cells.push({
      date: new Date(year, month - 1, day),
      isCurrentMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(year, month, day),
      isCurrentMonth: true,
    });
  }

  let nextMonthDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({
      date: new Date(year, month + 1, nextMonthDay),
      isCurrentMonth: false,
    });
    nextMonthDay += 1;
  }

  return cells;
};

const chunkWeeks = (cells) => {
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
};

const getRangeBarsForWeek = (weekCells, rangeEvents) => {
  const weekStart = startOfDay(weekCells[0].date);
  const weekEnd = startOfDay(weekCells[6].date);
  const bars = [];

  rangeEvents.forEach((event) => {
    const eventStart = startOfDay(parseIsoDate(event.start));
    const eventEnd = startOfDay(parseIsoDate(event.end));

    if (eventEnd < weekStart || eventStart > weekEnd) return;

    let startCol = null;
    let endCol = null;

    weekCells.forEach((cell, index) => {
      const day = startOfDay(cell.date);
      if (day >= eventStart && day <= eventEnd) {
        if (startCol === null) startCol = index;
        endCol = index;
      }
    });

    if (startCol === null) return;

    const span = endCol - startCol + 1;
    const barStartDay = startOfDay(weekCells[startCol].date);
    const barEndDay = startOfDay(weekCells[endCol].date);

    bars.push({
      id: event.id,
      event,
      title: event.title,
      color: event.color,
      highlight: event.highlight,
      startCol,
      span,
      showTitle: barStartDay.getTime() === eventStart.getTime(),
      isBarStart: barStartDay.getTime() === eventStart.getTime(),
      isBarEnd: barEndDay.getTime() === eventEnd.getTime(),
    });
  });

  return bars;
};

const EventDetailsPopover = ({ event, onClose, onRemove, canRemove }) => {
  const lines = getEventDetailLines(event);

  return (
    <div
      className="month-calendar-popover"
      role="dialog"
      aria-label={`${event.title} details`}
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" className="month-calendar-popover-close" onClick={onClose} aria-label="Close details">
        &times;
      </button>
      <p className="month-calendar-popover-title" style={{ borderLeftColor: event.color }}>
        {event.title}
      </p>
      <ul className="month-calendar-popover-list">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {canRemove && (
        <button type="button" className="month-calendar-popover-remove" onClick={onRemove}>
          Remove event
        </button>
      )}
    </div>
  );
};

const CalendarEventTrigger = ({ event, date, className, style, wrapStyle, children, pinnedKey, setPinnedKey, onRemoveEvent }) => {
  const key = getEventKey(event, date);
  const isPinned = pinnedKey === key;
  const [isHovered, setIsHovered] = useState(false);
  const showDetails = isPinned || isHovered;
  const classNames = [className, event.highlight ? 'is-highlight' : ''].filter(Boolean).join(' ');

  return (
    <div
      className={`month-calendar-event-wrap ${showDetails ? 'is-active' : ''}`}
      style={wrapStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className={classNames}
        style={style}
        onClick={(e) => {
          e.stopPropagation();
          setPinnedKey(isPinned ? null : key);
        }}
        aria-expanded={showDetails}
        aria-label={`${event.title}, show details`}
      >
        {children}
      </button>
      {showDetails && (
        <EventDetailsPopover
          event={event}
          onClose={() => {
            setPinnedKey(null);
            setIsHovered(false);
          }}
          canRemove={Boolean(onRemoveEvent)}
          onRemove={() => {
            onRemoveEvent?.(event);
            setPinnedKey(null);
            setIsHovered(false);
          }}
        />
      )}
    </div>
  );
};

const ChurchMonthCalendar = () => {
  const { isEditing } = useAuth();
  const { getContent, setContentValue } = useContent();
  const today = useMemo(() => startOfDay(new Date()), []);
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [pinnedKey, setPinnedKey] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );

  const customEvents = getContent('calendar.customEvents', []);
  const calendarEvents = useMemo(
    () => [...CHURCH_EVENTS, ...(Array.isArray(customEvents) ? customEvents : [])],
    [customEvents]
  );

  const removeCustomEvent = (event) => {
    if (!event.id?.startsWith('custom-')) return;
    if (!window.confirm(`Remove "${event.title}" from the calendar?`)) return;
    const nextEvents = customEvents.filter((item) => item.id !== event.id);
    setContentValue('calendar.customEvents', nextEvents);
  };

  const addCustomEvent = (event) => {
    const nextEvents = [...(Array.isArray(customEvents) ? customEvents : []), event];
    setContentValue('calendar.customEvents', nextEvents);

    const eventDate = parseIsoDate(event.start);
    setViewYear(eventDate.getFullYear());
    setViewMonth(eventDate.getMonth());
  };

  const defaultAddDate = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-01`;

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    onChange(mq);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const rangeEvents = useMemo(
    () => [
      ...getQuarterlyFastingEvents(viewYear),
      ...getRangeEvents(CHURCH_EVENTS),
      ...getRangeEvents(Array.isArray(customEvents) ? customEvents : []),
    ],
    [viewYear, customEvents]
  );

  const weeks = useMemo(() => {
    const cells = buildMonthGrid(viewYear, viewMonth);
    return chunkWeeks(cells);
  }, [viewYear, viewMonth]);

  useEffect(() => {
    if (!pinnedKey) return undefined;

    const handleOutsideClick = (e) => {
      if (!e.target.closest('.month-calendar-event-wrap')) {
        setPinnedKey(null);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setPinnedKey(null);
    };

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [pinnedKey]);

  const goToMonth = (offset) => {
    const next = new Date(viewYear, viewMonth + offset, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
    setPinnedKey(null);
  };

  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setPinnedKey(null);
  };

  return (
    <div className={`month-calendar ${isMobile ? 'is-mobile' : ''}`}>
      <div className="month-calendar-toolbar">
        <h3 className="month-calendar-heading">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h3>
        <div className="month-calendar-nav">
          <button type="button" className="month-calendar-nav-btn" onClick={() => goToMonth(-1)} aria-label="Previous month">
            &#10094;
          </button>
          <button type="button" className="month-calendar-today-btn" onClick={goToToday}>
            Today
          </button>
          <button type="button" className="month-calendar-nav-btn" onClick={() => goToMonth(1)} aria-label="Next month">
            &#10095;
          </button>
          {isEditing && (
            <button type="button" className="month-calendar-add-btn" onClick={() => setShowAddModal(true)}>
              + Add event
            </button>
          )}
        </div>
      </div>

      <AddCalendarEventModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={addCustomEvent}
        defaultDate={defaultAddDate}
      />

      <div className="month-calendar-weekdays">
        {(isMobile ? WEEKDAYS_MOBILE : WEEKDAYS).map((label) => (
          <div key={label} className="month-calendar-weekday">
            {label}
          </div>
        ))}
      </div>

      <div className="month-calendar-weeks">
        {weeks.map((weekCells, weekIndex) => {
          const rangeBars = getRangeBarsForWeek(weekCells, rangeEvents);

          return (
            <div key={`week-${weekIndex}`} className="month-calendar-week">
              {rangeBars.length > 0 && (
                <div className="month-calendar-range-row">
                  <div className="month-calendar-range-grid">
                    {rangeBars.map((bar) => (
                      <CalendarEventTrigger
                        key={`${bar.id}-${weekIndex}`}
                        event={bar.event}
                        date={parseIsoDate(bar.event.start)}
                        wrapStyle={{
                          gridColumn: `${bar.startCol + 1} / span ${bar.span}`,
                        }}
                        className={[
                          'month-calendar-range-bar',
                          bar.isBarStart ? 'bar-start' : 'bar-continue-left',
                          bar.isBarEnd ? 'bar-end' : 'bar-continue-right',
                        ].join(' ')}
                        style={{
                          '--event-color': bar.color,
                        }}
                        pinnedKey={pinnedKey}
                        setPinnedKey={setPinnedKey}
                        onRemoveEvent={isEditing ? removeCustomEvent : undefined}
                      >
                        {bar.showTitle && <span>{bar.title}</span>}
                      </CalendarEventTrigger>
                    ))}
                  </div>
                </div>
              )}

              <div className="month-calendar-week-days">
                {weekCells.map((cell, index) => {
                  const dayEvents = getCellEventsForDate(cell.date, calendarEvents);
                  const isToday = isSameDay(cell.date, today);
                  const visibleEvents = dayEvents.slice(0, isMobile ? 2 : 3);
                  const hiddenCount = dayEvents.length - visibleEvents.length;

                  return (
                    <div
                      key={`${cell.date.toISOString()}-${index}`}
                      className={`month-calendar-cell ${cell.isCurrentMonth ? '' : 'other-month'} ${isToday ? 'is-today' : ''}`}
                    >
                      <span className="month-calendar-date">{cell.date.getDate()}</span>
                      <div className="month-calendar-events">
                        {visibleEvents.map((event) => (
                          <CalendarEventTrigger
                            key={`${event.id}-${cell.date.toISOString()}`}
                            event={event}
                            date={cell.date}
                            className="month-calendar-event"
                            style={{ '--event-color': event.color }}
                            pinnedKey={pinnedKey}
                            setPinnedKey={setPinnedKey}
                            onRemoveEvent={isEditing ? removeCustomEvent : undefined}
                          >
                            <span className="month-calendar-event-title">{event.title}</span>
                            {(event.time || event.note) && (
                              <span className="month-calendar-event-meta">
                                {[event.time, event.note].filter(Boolean).join(' • ')}
                              </span>
                            )}
                          </CalendarEventTrigger>
                        ))}
                        {hiddenCount > 0 && (
                          <span className="month-calendar-more">+{hiddenCount} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChurchMonthCalendar;
